import { Button } from '@/components/ui/button'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

type PageProps = { message?: string | null }
const Page = ({ message }: PageProps) => (
  <div className="bg-background h-screen flex flex-col items-center justify-center p-4">
    <h1 className="dark:text-white text-4xl mb-4">Oops, something went wrong</h1>
    <p className="text-gray-600 dark:text-gray-300 text-lg text-center max-w-md">
      {message ||
        'We encountered an unexpected error while trying to join the meeting. Please try again or return home.'}
    </p>
    <div className="flex space-x-6 mt-8">
      <Link to="/">
        <Button variant="outline">Go to Home</Button>
      </Link>
    </div>
  </div>
)

type ErrorBoundaryProps = { children: React.ReactNode }

export const ErrorBoundary = ({ children }: ErrorBoundaryProps) => {
  const [hasError, setHasError] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // helper to filter noisy non-fatal errors
  const shouldIgnoreMessage = (msg: string) => {
    if (!msg) return false
    const lowers = msg.toLowerCase()
    return (
      lowers.includes('err_blocked_by_client') || // ad-block / analytics
      lowers.includes('cloudflare') ||
      lowers.includes('failed to fetch') || // transient network
      lowers.includes('networkerror') ||
      lowers.includes('load failed') ||
      lowers.includes('the operation was aborted') ||
      lowers.includes('unable to initiate video source') || // media device issues
      lowers.includes('permission denied') ||
      lowers.includes('notallowederror') ||
      lowers.includes('notfounderror') ||
      lowers.includes('overconstrainederror') ||
      lowers.includes('the play() request was interrupted by a new load request') ||
      lowers.includes('track ended') ||
      lowers.includes('invalidstateerror')
    )
  }

  useEffect(() => {
    const ignoredErrors = ['ResizeObserver loop completed with undelivered notifications.']

    const handleWindowError = (event: ErrorEvent) => {
      const msg = event.message || ''
      if (ignoredErrors.includes(msg) || shouldIgnoreMessage(msg)) return

      setHasError(true)
      setError(msg || 'An unexpected error occurred.')
      // no-op
    }

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason
      const msg = (reason && (reason.message || String(reason))) || 'An unexpected error occurred.'
      if (ignoredErrors.includes(msg) || shouldIgnoreMessage(msg)) return

      setHasError(true)
      setError(msg)
      // no-op
    }

    window.addEventListener('error', handleWindowError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)
    return () => {
      window.removeEventListener('error', handleWindowError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [])

  if (hasError) return <Page message={error} />
  return <>{children}</>
}

export const ErrorPage = () => <Page />
